<script setup lang="ts" generic="T extends Record<string, unknown>">
import Table from '@/components/Table.vue'
import type { GenericColumns } from '@/types/table'
import { ref, computed, watch } from 'vue'
import { useDisplay } from 'vuetify'

type SortOrder = 'asc' | 'desc'

type UpdatePayload = {
  page: number
  itemsPerPage: number
  searchQuery: string
  orderBy: string
  order: SortOrder
}

type ItemSlotProps = {
  item: T
  index?: number
  value?: unknown
} & Record<string, unknown>

type TableListSlots = {
  title?: () => unknown
  actions?: () => unknown
  selected?: () => unknown
  'mass-actions-content'?: () => unknown
  'empty-a'?: () => unknown
  'empty-b'?: () => unknown
} & {
  [key: `item.${string}`]: ((props: ItemSlotProps) => unknown) | undefined
}

defineSlots<TableListSlots>()

const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

const isCypress = computed(
  () => typeof window !== 'undefined' && !!window.Cypress
)

const props = withDefaults(
  defineProps<{
    title?: string
    addLabel?: string
    contentPadding?: number | string

    currentPage: number
    itemsPerPage?: number
    items?: T[]
    itemsLength?: number
    columns?: GenericColumns<T>

    showSelect?: boolean
    selectable?: boolean
    searchable?: boolean
    modelValue?: T[]
    showMassAction?: boolean
    rowClickable?: boolean
    searchQuery?: string
    loading?: boolean

    showSort?: boolean
    sortOptions?: { value: string; label: string }[]
    sortBy?: string
    sortOrder?: SortOrder

    showFilter?: boolean

    simpleMode?: boolean
  }>(),
  {
    title: '',
    addLabel: '',
    contentPadding: 24,
    currentPage: 1,
    itemsPerPage: 10,
    items: () => [] as T[],
    itemsLength: 0,
    columns: () => [] as unknown as GenericColumns<T>,
    showSelect: false,
    selectable: false,
    searchable: false,
    modelValue: () => [] as T[],
    showMassAction: false,
    rowClickable: true,
    searchQuery: '',
    loading: false,
    showSort: false,
    sortOptions: () => [],
    sortBy: '',
    sortOrder: 'asc',
    showFilter: false,
    simpleMode: false,
  }
)

const emit = defineEmits<{
  (e: 'delete-item', item: T): void
  (e: 'detail-item', item: T): void
  (e: 'update', payload: UpdatePayload): void
  (e: 'selection-change', value: T[]): void
  (e: 'update:modelValue', value: T[]): void
  (e: 'update:searchQuery', value: string): void
  (e: 'update-item', item: T): void
  (e: 'update:currentPage', value: number): void
  (e: 'update:itemsPerPage', value: number): void
  (e: 'add-click'): void
  (e: 'update:sort-by', value: string): void
  (e: 'update:sort-order', value: SortOrder): void
  (e: 'filter-click'): void
}>()

function onDeleteItem(p: T) {
  emit('delete-item', p)
}
function onDetailItem(p: T) {
  emit('detail-item', p)
}
function onUpdate(p: UpdatePayload) {
  emit('update', p)
}
function onSelectionChange(p: T[]) {
  emit('selection-change', p)
}
function onUpdateModelValue(p: T[]) {
  emit('update:modelValue', p)
}
function onUpdateSearchQuery(p: string) {
  emit('update:searchQuery', p)
}
function onUpdateItem(p: T) {
  emit('update-item', p)
}
function onUpdateCurrentPage(p: number) {
  emit('update:currentPage', p)
}
function onUpdateItemsPerPage(p: number) {
  emit('update:itemsPerPage', p)
}

const sortMenuSearch = ref(false)

function applySort(newOrderBy: string) {
  const currentBy = props.sortBy ?? ''
  const currentOrder: SortOrder = props.sortOrder ?? 'asc'

  let nextOrder: SortOrder = currentOrder

  if (currentBy === newOrderBy) {
    nextOrder = currentOrder === 'asc' ? 'desc' : 'asc'
  } else {
    nextOrder = 'asc'
    emit('update:sort-by', newOrderBy)
  }

  emit('update:sort-order', nextOrder)

  emit('update', {
    page: 1,
    itemsPerPage: props.itemsPerPage,
    searchQuery: props.searchQuery ?? '',
    orderBy: newOrderBy,
    order: nextOrder,
  })
}

const pageCount = computed(() => {
  const total = props.itemsLength ?? 0
  const per =
    props.itemsPerPage && props.itemsPerPage > 0 ? props.itemsPerPage : 1
  return total > 0 ? Math.ceil(total / per) : 0
})

const showMobilePagination = computed(
  () => isMobile.value && pageCount.value > 1
)

watch(
  () => pageCount.value,
  (len) => {
    if (!isMobile.value) return
    const safeLen = len > 0 ? len : 1
    if (props.currentPage > safeLen) emit('update:currentPage', safeLen)
    if (props.currentPage < 1) emit('update:currentPage', 1)
  },
  { immediate: true }
)

const useSimple = computed(() => props.simpleMode || isCypress.value)

function getCellValue(item: T, key: string): string {
  const v = item[key]
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)

  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}
</script>

<template>
  <div class="pa-0 mt-n1">
    <div class="d-flex align-center justify-space-between">
      <slot v-if="title && !isMobile" name="title">
        <span style="font-weight: 600; font-size: 20px; opacity: 0.8">
          {{ title }}
        </span>
      </slot>

      <slot name="actions" />
    </div>

    <div>
      <Table
        v-if="!useSimple"
        v-bind="{
          currentPage,
          itemsPerPage,
          items,
          itemsLength,
          columns,
          showSelect,
          selectable,
          searchable,
          modelValue,
          showMassAction,
          rowClickable,
          searchQuery,
          loading,
        }"
        :class="['listlike', 'no-row-dividers', isMobile ? 'mt-1' : 'mt-n2']"
        @delete-item="onDeleteItem"
        @detail-item="onDetailItem"
        @update="onUpdate"
        @selection-change="onSelectionChange"
        @update:model-value="onUpdateModelValue"
        @update:search-query="onUpdateSearchQuery"
        @update-item="onUpdateItem"
        @update:current-page="onUpdateCurrentPage"
        @update:items-per-page="onUpdateItemsPerPage"
      >
        <template #search-append>
          <template v-if="showSort && sortOptions?.length">
            <v-menu
              v-model="sortMenuSearch"
              location="bottom start"
              transition="fade-transition"
            >
              <template #activator="{ props: vprops }">
                <v-btn
                  v-bind="vprops"
                  variant="text"
                  icon="mdi-swap-vertical"
                  size="36"
                  class="ml-2"
                  style="opacity: 0.6"
                  @click.stop
                />
              </template>

              <v-list density="comfortable" variant="text">
                <v-list-item
                  v-for="opt in sortOptions"
                  :key="opt.value"
                  @click="applySort(opt.value)"
                >
                  <v-list-item-title>{{ opt.label }}</v-list-item-title>
                  <template #append>
                    <v-icon
                      v-if="sortBy == opt.value"
                      size="small"
                      class="ml-n4"
                    >
                      {{
                        sortOrder == 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'
                      }}
                    </v-icon>
                  </template>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <v-btn
            v-if="showFilter"
            id="filter-button"
            variant="text"
            icon="mdi-filter-variant"
            size="small"
            @click.stop="$emit('filter-click')"
          />
        </template>

        <template
          v-for="col in columns"
          :key="String(col.key)"
          #[`item.${String(col.key)}`]="slotProps"
        >
          <slot
            :name="`item.${String(col.key)}`"
            v-bind="slotProps as unknown as ItemSlotProps"
          />
        </template>

        <template #selected><slot name="selected" /></template>
        <template #mass-actions-content
          ><slot name="mass-actions-content"
        /></template>
        <template #empty-a><slot name="empty-a" /></template>
        <template #empty-b><slot name="empty-b" /></template>
      </Table>

      <!-- ✅ fallback: modo simples (Cypress) -->
      <div v-else class="simple-list mt-2">
        <!-- loading -->
        <div v-if="loading" class="pa-4 text-body-2" style="opacity: 0.7">
          Carregando...
        </div>

        <!-- empty -->
        <div v-else-if="!items?.length" class="pa-4">
          <slot name="empty-a" />
          <slot name="empty-b" />
          <div
            v-if="!$slots['empty-a'] && !$slots['empty-b']"
            class="text-body-2"
            style="opacity: 0.7"
          >
            Nenhum item.
          </div>
        </div>

        <!-- rows -->
        <div v-else class="d-flex flex-column ga-2">
          <div
            v-for="(row, index) in items"
            :key="String((row as Record<string, unknown>)?.id ?? index)"
            class="simple-row pa-3"
            :data-cy="`simple-row-${index}`"
            @click="rowClickable ? onUpdateItem(row) : undefined"
          >
            <div
              v-for="col in columns"
              :key="String(col.key)"
              class="d-flex align-center justify-space-between ga-4 py-1"
            >
              <div class="text-caption" style="opacity: 0.65">
                {{ String(col.title ?? col.key) }}
              </div>

              <div class="text-body-2 text-right" style="max-width: 65%">
                <slot
                  v-if="$slots[`item.${String(col.key)}`]"
                  :name="`item.${String(col.key)}`"
                  :item="row"
                  :index="index"
                  :value="(row as Record<string, unknown>)[String(col.key)]"
                />
                <template v-else>
                  {{ getCellValue(row, String(col.key)) }}
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- pagination -->
        <div v-if="pageCount > 1" class="d-flex justify-center pt-3">
          <v-pagination
            :model-value="props.currentPage"
            :length="pageCount"
            :total-visible="5"
            show-first-last-page
            first-icon="mdi-page-first"
            last-icon="mdi-page-last"
            prev-icon="mdi-chevron-left"
            next-icon="mdi-chevron-right"
            density="comfortable"
            @update:modelValue="onUpdateCurrentPage"
          />
        </div>
      </div>

      <!-- mantém paginação mobile original (quando usa Table e ela esconde footer) -->
      <div v-if="showMobilePagination && !useSimple" class="mobile-pagination">
        <v-pagination
          :model-value="props.currentPage"
          :length="pageCount"
          :total-visible="5"
          show-first-last-page
          first-icon="mdi-page-first"
          last-icon="mdi-page-last"
          prev-icon="mdi-chevron-left"
          next-icon="mdi-chevron-right"
          density="comfortable"
          @update:modelValue="onUpdateCurrentPage"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.v-btn:hover {
  background-color: #e0e0e0 !important;
}
:deep(.listlike .v-data-table__thead) {
  display: none !important;
}
:deep(.listlike.no-row-dividers .v-data-table__td),
:deep(.listlike.no-row-dividers .v-data-table__tr) {
  border-bottom: none !important;
}

/* fallback styles */
.simple-row {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #fff;
}

@media (max-width: 600px) {
  :deep(.listlike .v-data-table-footer),
  :deep(.listlike .v-data-table__bottom) {
    display: none !important;
  }
  .mobile-pagination {
    display: flex;
    justify-content: center;
    padding: 12px 0 0;
  }
}

@media (min-width: 601px) {
  :deep(.listlike .v-data-table-footer),
  :deep(.listlike .v-data-table__bottom) {
    display: flex !important;
    justify-content: flex-end !important;
    align-items: center;
    width: 100%;
  }
  .mobile-pagination {
    display: none;
  }
}

:deep(.mobile-pagination .v-pagination__item--is-active),
:deep(.mobile-pagination .v-pagination .v-btn) {
  border-radius: 10px;
}
:deep(.mobile-pagination .v-pagination__item--is-active) {
  color: white !important;
}
</style>
